import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { toApiError } from '@/services/api/responseHandler';
import { mediaService, type GetBooksParams } from '@/services/media/mediaService';
import type { BookOrder, BookOrdersMeta, BookReceipt, Bookmark, CreateBookmarkPayload, LibraryItem, MediaBook, MediaBookDetail, MediaMeta, ReadingProgress, ReaderContent, ReaderSession, SaveReadingProgressPayload } from '@/types/media';

export interface MediaBooksData {
  items: MediaBook[];
  meta: MediaMeta;
}

export interface GetOrdersParams {
  page?: number;
  limit?: number;
  status?: string;
}

export interface MediaOrdersData {
  items: BookOrder[];
  meta: BookOrdersMeta;
}

/**
 * RTK Query API slice for the media feature.
 *
 * - Reads go through the existing axios-based `mediaService`, so auth
 *   interceptors and API error mapping keep working unchanged.
 * - Cached data is served instantly on remount; a background refetch only
 *   happens when cached data is older than 60s (RTK Query default staleness).
 */
export const mediaApi = createApi({
  reducerPath: 'mediaApi',
  baseQuery: fakeBaseQuery<ReturnType<typeof toApiError>>(),
  tagTypes: ['Book', 'Order', 'Receipt', 'Library', 'ReadingProgress', 'Bookmark'],
  endpoints: (builder) => ({
    /**
     * Books list with infinite scroll support.
     *
     * The cache key intentionally excludes `page`, so all pages for a given
     * tab/search combination live in a single cache entry and pages are
     * merged together as they arrive. Navigating back to /media restores the
     * accumulated list instantly without hitting the network.
     */
    getBooks: builder.query<MediaBooksData, GetBooksParams>({
      queryFn: async (params) => {
        try {
          const response = await mediaService.getBooks(params);
          return { data: response.data };
        } catch (error) {
          return { error: toApiError(error) };
        }
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { type = 'all', search = '', conventionId } = queryArgs;
        return `${endpointName}-${type}-${search.trim()}-${conventionId ?? ''}`;
      },
      merge: (current, incoming, { arg }) => {
        if ((arg.page ?? 1) > 1) {
          const seenIds = new Set(current.items.map((book) => book.id));
          current.items.push(...incoming.items.filter((book) => !seenIds.has(book.id)));
        } else {
          current.items = incoming.items;
        }
        current.meta = incoming.meta;
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        JSON.stringify(currentArg ?? {}) !== JSON.stringify(previousArg ?? {}),
      providesTags: ['Book'],
      keepUnusedDataFor: 300,
    }),

    /**
     * Orders list with infinite scroll support.
     *
     * Cache key includes `status` so switching tabs returns instantly from
     * cache. Pages within the same status are merged into one entry.
     */
    getOrders: builder.query<MediaOrdersData, GetOrdersParams>({
      queryFn: async (params) => {
        try {
          const response = await mediaService.getOrders(params);
          return { data: response.data };
        } catch (error) {
          return { error: toApiError(error) };
        }
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { status = '' } = queryArgs;
        return `${endpointName}-${status}`;
      },
      merge: (current, incoming, { arg }) => {
        if ((arg.page ?? 1) > 1) {
          const seenIds = new Set(current.items.map((o) => o.id));
          current.items.push(...incoming.items.filter((o) => !seenIds.has(o.id)));
        } else {
          current.items = incoming.items;
        }
        current.meta = incoming.meta;
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        JSON.stringify(currentArg ?? {}) !== JSON.stringify(previousArg ?? {}),
      providesTags: ['Order'],
      keepUnusedDataFor: 300,
    }),

    getOrderDetail: builder.query<BookOrder, string>({
      queryFn: async (orderId) => {
        try {
          const response = await mediaService.getOrderDetail(orderId);
          return { data: response.data };
        } catch (error) {
          return { error: toApiError(error) };
        }
      },
      providesTags: (_result, _error, orderId) => [{ type: 'Order', id: orderId }],
      keepUnusedDataFor: 300,
    }),

    getBookReceipt: builder.query<BookReceipt, string>({
      queryFn: async (reference) => {
        try {
          const response = await mediaService.getBookReceipt(reference);
          return { data: response.data };
        } catch (error) {
          return { error: toApiError(error) };
        }
      },
      providesTags: (_result, _error, ref) => [{ type: 'Receipt', id: ref }],
      keepUnusedDataFor: 300,
    }),

    getLibrary: builder.query<{ items: LibraryItem[]; meta: BookOrdersMeta }, { page?: number; limit?: number }>({
      queryFn: async (params) => {
        try {
          const response = await mediaService.getLibrary(params);
          return { data: response.data };
        } catch (error) {
          return { error: toApiError(error) };
        }
      },
      serializeQueryArgs: () => 'getLibrary',
      merge: (current, incoming, { arg }) => {
        if ((arg.page ?? 1) > 1) {
          const seenIds = new Set(current.items.map((item) => item.id));
          current.items.push(...incoming.items.filter((item) => !seenIds.has(item.id)));
        } else {
          current.items = incoming.items;
        }
        current.meta = incoming.meta;
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        JSON.stringify(currentArg ?? {}) !== JSON.stringify(previousArg ?? {}),
      providesTags: ['Library'],
      keepUnusedDataFor: 300,
    }),

    getBookDetail: builder.query<MediaBookDetail, string>({
      queryFn: async (bookId) => {
        try {
          const response = await mediaService.getBookDetail(bookId);
          return { data: response.data };
        } catch (error) {
          return { error: toApiError(error) };
        }
      },
      providesTags: (_result, _error, bookId) => [{ type: 'Book', id: bookId }],
      keepUnusedDataFor: 300,
    }),

    /* ---------- Reader ---------- */

    createReaderSession: builder.mutation<ReaderSession, string>({
      queryFn: async (entitlementId) => {
        try {
          const response = await mediaService.createReaderSession(entitlementId);
          return { data: response.data };
        } catch (error) {
          return { error: toApiError(error) };
        }
      },
    }),

    getReaderContent: builder.query<ReaderContent, string>({
      queryFn: async (contentEndpoint) => {
        try {
          const response = await mediaService.getReaderContent(contentEndpoint);
          return { data: response.data };
        } catch (error) {
          return { error: toApiError(error) };
        }
      },
      keepUnusedDataFor: 300,
    }),

    getReadingProgressList: builder.query<ReadingProgress[], void>({
      queryFn: async () => {
        try {
          const response = await mediaService.getReadingProgress();
          return { data: response.data };
        } catch (error) {
          return { error: toApiError(error) };
        }
      },
      providesTags: ['ReadingProgress'],
      keepUnusedDataFor: 300,
    }),

    saveReadingProgress: builder.mutation<unknown, SaveReadingProgressPayload>({
      queryFn: async (payload) => {
        try {
          await mediaService.saveReadingProgress(payload);
          return { data: null };
        } catch (error) {
          return { error: toApiError(error) };
        }
      },
      invalidatesTags: ['ReadingProgress'],
    }),

    getBookmarks: builder.query<Bookmark[], string>({
      queryFn: async (entitlementId) => {
        try {
          const response = await mediaService.getBookmarks(entitlementId);
          return { data: response.data };
        } catch (error) {
          return { error: toApiError(error) };
        }
      },
      providesTags: (_result, _error, entitlementId) => [
        { type: 'Bookmark', id: entitlementId },
      ],
      keepUnusedDataFor: 300,
    }),

    createBookmark: builder.mutation<Bookmark, CreateBookmarkPayload>({
      queryFn: async (payload) => {
        try {
          const response = await mediaService.createBookmark(payload);
          return { data: response.data };
        } catch (error) {
          return { error: toApiError(error) };
        }
      },
      invalidatesTags: (_result, _error, payload) => [
        { type: 'Bookmark', id: payload.entitlementId },
      ],
    }),
  }),
});

export const {
  useGetBooksQuery,
  useGetOrdersQuery,
  useGetOrderDetailQuery,
  useGetBookReceiptQuery,
  useLazyGetBookReceiptQuery,
  useGetLibraryQuery,
  useGetBookDetailQuery,
  useCreateReaderSessionMutation,
  useGetReaderContentQuery,
  useLazyGetReaderContentQuery,
  useGetReadingProgressListQuery,
  useSaveReadingProgressMutation,
  useGetBookmarksQuery,
  useCreateBookmarkMutation,
} = mediaApi;
